import {
  summarizeFinance,
  type ProjectInvoice,
  type ProjectPayment,
} from '../finance/projectFinance';

export interface BookProject {
  id: string;
  name: string;
  status: string;
  product_line_id: string | null;
  value_amount: number;
}

export interface BookLine {
  id: string;
  name: string;
  sort_order: number;
}

export interface Rollup {
  value: number;
  invoiced: number;
  received: number;
  outstanding: number;
  toCollect: number;
  minutes: number;
  activeProjects: number;
}

export interface BookProjectRow extends BookProject {
  received: number;
  outstanding: number;
  minutes: number;
}

export interface BookGroup {
  /** null = projects with no line. */
  line: BookLine | null;
  projects: BookProjectRow[];
  rollup: Rollup;
}

export interface CustomerBook {
  groups: BookGroup[];
  total: Rollup;
}

const INACTIVE = new Set(['completed', 'cancelled']);

function rollupOf(
  projects: BookProject[],
  invoices: Pick<ProjectInvoice, 'id' | 'project_id' | 'amount' | 'status'>[],
  payments: Pick<ProjectPayment, 'project_id' | 'invoice_id' | 'amount'>[],
  minutes: Record<string, number>,
): Rollup {
  const ids = new Set(projects.map((p) => p.id));
  const s = summarizeFinance(
    projects.reduce((sum, p) => sum + p.value_amount, 0),
    invoices.filter((i) => ids.has(i.project_id)),
    payments.filter((p) => ids.has(p.project_id)),
  );
  return {
    value: s.value,
    invoiced: s.invoiced,
    received: s.received,
    outstanding: s.outstanding,
    toCollect: s.toCollect,
    minutes: projects.reduce((sum, p) => sum + (minutes[p.id] ?? 0), 0),
    activeProjects: projects.filter((p) => !INACTIVE.has(p.status)).length,
  };
}

/**
 * A customer's projects grouped by product line, each group and the whole
 * customer rolled up. Lines with no projects still appear, so a new line has a
 * place to add its first project. Projects pointing at a line that is not in
 * `lines` (deleted, or another customer's) fall into the no-line group.
 */
export function buildCustomerBook(
  projects: BookProject[],
  lines: BookLine[],
  invoices: Pick<ProjectInvoice, 'id' | 'project_id' | 'amount' | 'status'>[],
  payments: Pick<ProjectPayment, 'project_id' | 'invoice_id' | 'amount'>[],
  minutes: Record<string, number>,
): CustomerBook {
  const lineIds = new Set(lines.map((l) => l.id));
  const row = (p: BookProject): BookProjectRow => {
    const r = rollupOf([p], invoices, payments, minutes);
    return { ...p, received: r.received, outstanding: r.outstanding, minutes: r.minutes };
  };

  const ordered = [...lines].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'he'),
  );
  const groups: BookGroup[] = ordered.map((line) => {
    const own = projects.filter((p) => p.product_line_id === line.id);
    return { line, projects: own.map(row), rollup: rollupOf(own, invoices, payments, minutes) };
  });

  const loose = projects.filter((p) => !p.product_line_id || !lineIds.has(p.product_line_id));
  if (loose.length > 0) {
    groups.push({
      line: null,
      projects: loose.map(row),
      rollup: rollupOf(loose, invoices, payments, minutes),
    });
  }

  return { groups, total: rollupOf(projects, invoices, payments, minutes) };
}
